import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tappable } from '@vectord/components';
import cogoToast from 'cogo-toast';
import { TypeUtils } from '@vectord/utils';

import { DEFAULT_ERROR_MESSAGE } from 'data/defaultErrorMessage';
import {
  generateBulkUpdateSbatGroupsInstructors,
  generatePreviousSbatGroupsInstructorIds,
  resetSbatGroupBulkUpdate,
  selectedBulkUpdateSbatGroupsSelector,
  selectedBulkUpdateContestSbatGroupsSelector,
  useBulkUpdateSbatGroupInstructorsMutation,
  validateBulkUpdateInstructor,
  VIEW_SCHEDULE_PAGE_TRACKINGS,
} from 'features/classCreation';
import analytics from 'utils/analytics';
import FieldErrors from 'components/FieldErrors';
import GlobalLoader from 'components/GlobalLoader';

function BulkUpdateInstructorsButton({
  primaryInstructor,
  secondaryInstructor,
  otherInstructors,
}) {
  const dispatch = useDispatch();
  const selectedSbatGroups = useSelector(selectedBulkUpdateSbatGroupsSelector);
  const [updateErrors, setUpdateErrors] = useState(null);
  const contestSbatGroups = useSelector(
    selectedBulkUpdateContestSbatGroupsSelector
  );
  const [inputErrors, setInputErrors] = useState(null);
  const [bulkUpdateInstructors, { isSuccess, error, isLoading: isUpdating }] =
    useBulkUpdateSbatGroupInstructorsMutation();

  const handleUpdate = useCallback(() => {
    const bulkUpdatedSbatGroupInstructors =
      generateBulkUpdateSbatGroupsInstructors({
        primaryInstructor,
        secondaryInstructor,
        otherInstructors,
      });

    const errors = validateBulkUpdateInstructor({
      primaryInstructor,
      contestSbatGroups,
    });

    if (Object.keys(errors).length === 0) {
      setInputErrors({});
      const sbatGroupIds = Object.keys(selectedSbatGroups);
      const newOtherInstructorIds = otherInstructors?.map(
        (instructor) => instructor?.value
      );
      const {
        previousPrimaryInstructorIds,
        previousSecondaryInstructorIds,
        previousOtherInstructorIds,
      } = generatePreviousSbatGroupsInstructorIds(selectedSbatGroups);

      analytics.click({
        click_type: VIEW_SCHEDULE_PAGE_TRACKINGS.bulkUpdateInstructors,
        custom: {
          class_ids: sbatGroupIds,
          num_of_classes: sbatGroupIds.length,
          previous_primary_instructor_ids: previousPrimaryInstructorIds,
          previous_secondary_instructor_ids: previousSecondaryInstructorIds,
          previous_other_instructor_ids: previousOtherInstructorIds,
          new_primary_instructor_id: primaryInstructor.value,
          new_secondary_instructor_id: secondaryInstructor?.value,
          new_other_instructor_ids: newOtherInstructorIds,
        },
      });

      bulkUpdateInstructors({
        sbatGroupIds: sbatGroupIds,
        sbatGroupInstructors: bulkUpdatedSbatGroupInstructors,
      });
    } else {
      setInputErrors(errors);
    }
  }, [
    primaryInstructor,
    secondaryInstructor,
    otherInstructors,
    contestSbatGroups,
    selectedSbatGroups,
    bulkUpdateInstructors,
  ]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(resetSbatGroupBulkUpdate());
      cogoToast.success('Instructor is successfully updated!');
    }
  }, [dispatch, isSuccess]);

  useEffect(() => {
    if (error) {
      const message = error.data?.message;

      if (message?.base && TypeUtils.isArray(message.base)) {
        setUpdateErrors({ base: message.base });
      } else {
        cogoToast.error(message || DEFAULT_ERROR_MESSAGE);
      }
    }
  }, [error]);

  return (
    <>
      <GlobalLoader isVisible={isUpdating} />
      <FieldErrors
        name="primaryInstructor"
        errors={inputErrors}
      />
      <FieldErrors
        name="base"
        errors={updateErrors}
      />
      <FieldErrors
        name="contest"
        errors={inputErrors}
      />
      <Tappable
        className="btn btn-primary m-b-xxs m-t-xs"
        disabled={isUpdating}
        onClick={handleUpdate}
      >
        Apply Changes
      </Tappable>
    </>
  );
}

export default BulkUpdateInstructorsButton;
